<script>
export default {
  title: 'Project 4: Turtle Arena',
  starterId: null
}
</script>

<template>
  <section>
    <article>
      <h2>Background</h2>

      <p>
        This project is based on your work from the previous project.
      </p>

      <p>
        At this point you are the proud author of a delightful cat and mouse simulation whose vanilla
        Arena Interface was given to you.
      </p>
    </article>

    <article>
      <h2>Problem</h2>
      <p>
        You are asked to augment the basic Arena user interface and add features to it which would be
        useful. In the process, you will learn about tkInter. Add the following:
      </p>

      <ul>
        <li>
          <p>
            <strong>A reset Button</strong> to the Canvas (place it to the left of the 'step' button)
            - Clicking this stops the simulation (if it's running) and resets everything (cat, mouse,
            system) to the original values
          </p>
        </li>
        <li>
          <p>
            <strong>A time Label</strong> widget to the Canvas (how
            many steps [minutes, in this case] we are in the simulation) that reads: <strong>Time</strong>:
            <em>&lt;current-time&gt;</em>
          </p>
        </li>
        <li>
          <p><strong>A Cat radius Label</strong> widget to the Canvas (indicating the current Cat's radius
            [in meters, not pixels]) that reads: <strong>CatRadius</strong>: <em>&lt;value&gt;</em>
          </p>
        </li>
        <li>
          <p><strong>A Cat angle Label</strong> widget to the Canvas (indicating the current Cat's angle
            [radians or degrees is ok]) that reads: <strong>CatAngle</strong>: <em>&lt;value&gt;</em>
          </p>
        </li>
        <li>
          <p>
            <strong>A Mouse angle Label</strong> to the Canvas (indicating the current Mouse's angle
            [radians or degrees is ok]) that reads: <strong>MouseAngle</strong>: <em>&lt;value&gt;</em>
          </p>
        </li>
        <li>
          <p>
            <strong>A Menu </strong>widget item at the top which says &quot;File&quot; and when selected,
            shows &quot;About...&quot; and &quot;Quit&quot;
          </p>
        <li>

        <ul>
          <li>
            When Quit is selected, the simulation quits (same behavior as clicking the button on the canvas)
          </li>
          <li>
            When About... is selected, a new window should pop up which has the following widgets in it:
            <ul>
              <li>
                <p>
                  A Label widget which says your name and the name of the project and includes anything
                  else you want to add
                </p>
              <li>
              <p>
                A PhotoImage widget with a picture of you
              </p>
              <li>
                <p>
                  A button at the bottom labeled &quot;Ok&quot;, which closes the new window when clicked
                </p>
              <li>
                <p>
                  The title of the new window should read something appropriate, like &quot;About the UC
                  Berkeley CS9H Turtle Arena&quot;
                </p>
              </li>
            </ul>
          </li>
        </ul>

        <li>
          <p>
            <strong>The ability to move the cat when the simulation is stopped</strong>. When your cursor
            moves over the cat, it should turn black (to provide feedback to the user). If the user clicks
            their mouse button when the cursor is over the cat, and drags their mouse, the cat should follow
            (as if you were moving a plate around on a table with your hand). The cat's orientation should
            always be facing the center of the statue as it's being moved around. The values in the cat
            radius and angle Labels should update as you move. Releasing the mouse button should release
            the connection your mouse movement has with the cat graphic object. Moving the cursor (with the
            mouse released) off of the cat should return the cat's color to its original value. This allows
            you to place the cat anywhere on screen; don't you wish you had this feature when you were testing
            project 4?
          </p>
        <li>
          <p>
            <strong>One more widget of your choosing</strong> (from the following list) that should serve
            a useful purpose for your simulation:
          </p>
          <ul>
            <li>
              Checkbox (perhaps toggling some feature of the display, like turning off the printed logging
              of the simulation values to the screen?)
            </li>
            <li>
              Radiobutton (perhaps controlling the clockwise vs counterclockwise motion of the mouse?)
            </li>
            <li>
              Listbox (perhaps to choose the color of the cat/mouse?)
            </li>
            <li>
              Entries and spinboxes (perhaps to explicitly edit the cat/mouse parameters?)
            </li>
            <li>
              Scales (perhaps to adjust how many minutes the cat will chase before it quits [default=30])
            </li>
          </ul>
        </li>
      </ul>
    </article>

    <article>
      <h2>Hints</h2>

      <p>Here are some general tips that may help you in your journey:</p>

      <ol>
        <li>Make a copy of your code from project 4 before you start modifying the Arena and related classes.</li>
        <li>You will probably have to modify more than just <code>libs/Arena.py</code> to add all the features we're requesting.</li>
        <li>Feel free to add any supplementary files you think will be needed.</li>
        <li>We've intentionally not provided any sample screenshots because we want you to have full flexibility over the layout of graphical elements within the GUI.</li>
      </ol>
    </article>

    <article>
      <h2>Checklist</h2>
      <ul>
        <li>Correctly working code</li>
        <li>Use of functions &mdash; your program must be broken down into one or more functions &mdash; it cannot be one big long script.</li>

        <li>Sufficient testing, with output sufficient to verify test correctness. Testing a GUI is harder than a command-line interface (CLI). You should:</li>
          <ul>
            <li>You should also try to break your GUI. What happens if:
              <ul>
                <li>...you select your cat when the simulation is running?</li>
                <li>...you open multiple windows by selecting &quot;About...&quot; several times? Maybe you want to <em>disable</em> the About... menu item if the About window is still open?</li>
                <li>...you move the Cat <em>inside</em> the statue and start the simulation? Maybe you want to constrain its movement to prevent this? (The cat's radius would then simply be the max of the radius of the statue and the cursor position.)</li>

                <li>...you move the Cat and then click reset. Should the Cat revert to its original, pre-moved position?</li>
              </ul>
            </li>
          </ul>

        <li>Good style
          <ul>
            <li>appropriate use of indenting and white space;</li>
            <li>variable and method names that reflect their use;</li>
            <li>informative comments at the head of each method;</li>
          </ul>
        </li>
      </ul>
      <h2><a id="extra" name="extra"></a>Extra for Experts</h2>
      <p>With GUIs, there are always tons of things you could add. Here are some things you could do to have some fun:</p>
      <ul>
        <li>Add a Scale widget that will (in real-time) adjust the initial pixels-per-m scale.</li>
        <li>Add a Scale widget that allows you to &quot;scratch&quot; your way back and forth through simulation time, like you'd do for a Quicktime movie.</li>
        <li>Allow the user to move the mouse object too. It should always stay constrained to lie in the circle of the mouse.</li>
        <li>Allow the user to double-click in the canvas to add another cat.
        <li> After the cat is added, it should also display its parameters (radius, angle) on the canvas. The user should be able to move those out of the way.</li>
      </ul>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/python/TurtleArena.vue